import {EntityModel} from "../model";
import {Product} from "../products/model";
import {getProductBrowseURL} from "./fileserver/Util";
import {defineMessages, InjectedIntl} from "react-intl";

export const WMS_SERVICE_TYPE = "wms";
export const WMTS_SERVICE_TYPE = "wmts";
export const WFS_SERVICE_TYPE = "wfs";
export const WCS_SERVICE_TYPE = "wcs";
export const CSW_SERVICE_TYPE = "csw";
export const LTS_SERVICE_TYPE = "lts";
export const FILE_SERVER_SERVICE_TYPE = "fileserver";
export const OGC3DTILES_SERVICE_TYPE = "ogc3dtiles";

export enum ServiceStatus {
  RUNNING = "Running",
  PENDING = "Pending",
  STOPPED = "Stopped",
}

export enum MeshCompression {
  NONE = "None",
  DRACO = "Draco",
}

export enum AccessMode {
  DEFAULT = "Default",
  AUTHENTICATED = "Authenticated",
  ANONYMOUS = "Anonymous",
}

export enum PointCloudCompression {
  NONE = "None",
  DRACO = "Draco",
}

export enum TileSetVersion {
  VERSION_1_0 = "1.0",
  VERSION_1_1 = "1.1",
}

export interface ContactInfo {
  individualName?: string;
  organizationName?: string;
  position?: string;
  deliveryPoint?: string;
  city?: string;
  administrativeArea?: string;
  postCode?: string;
  country?: string;
  fax?: string;
  phone?: string;
  email?: string;
}

export interface Service extends EntityModel {
  type: string;
  status?: ServiceStatus;
  creationTime?: string;
  updateTime?: string;
  startedTime?: string;
  accessMode?: AccessMode;
  createdBy?: {
    username: string;
  };
  endpointPath?: string;
  products?: Product[];
  canDelete: boolean;
  preprocessingOutputPath?: string;
  endpointUrl?: string;
  meshCompression?: MeshCompression;
  pointCloudCompression?: PointCloudCompression;
  tileSetVersion?: TileSetVersion;
  wfsTransactionsEnabled?: boolean;
  accessConstraint?: string;
  contactInformation?: ContactInfo;
}

export interface ServiceTypeDetails {
  serviceType: string;
  maxAllowedProductCount?: number;
  hasCapabilities: boolean;
  needsPreprocessing: boolean;
}

export const toServiceLink = (service: Service) => `/services/${service.id}`;

export const canRemoveService = (service: Service) => {
  return service.canDelete;
};

export const supportsProducts = (service: Service) => {
  return service && service.type !== CSW_SERVICE_TYPE;
};

export const supportsAccessConstraints = (serviceType: string) => {
  return serviceType
         && (serviceType === WMS_SERVICE_TYPE
             || serviceType === WMTS_SERVICE_TYPE
             || serviceType === WFS_SERVICE_TYPE
             || serviceType === WCS_SERVICE_TYPE);
}

export const supportsContactInfo = (serviceType: string) => {
  return serviceType
         && (serviceType === WMS_SERVICE_TYPE
             || serviceType === WMTS_SERVICE_TYPE
             || serviceType === WFS_SERVICE_TYPE
             || serviceType === WCS_SERVICE_TYPE);
}

export const hasEndPointURL = (service: Service) => {
  if (service) {
    if (service.type === FILE_SERVER_SERVICE_TYPE) {
      return service.products !== null && service.products !== undefined && service.products.length === 1;
    }
    return true;
  }
  return false;
};

export const getEndPointURL = (service: Service, products?: Product[]) => {
  if (service && service.type === FILE_SERVER_SERVICE_TYPE) {
    if (products && products.length === 1) {
      const product = products[0];
      return product && getProductBrowseURL(service, product);
    }
  }
  return service.endpointUrl;
};

export const isWFS = (serviceType: string) => {
  return serviceType && serviceType === WFS_SERVICE_TYPE;
};

const ACCESS_MODE_MESSAGES = defineMessages({
  default: {
    id: "studio.access-mode-option.default-label",
    defaultMessage: "Default",
  },
  anonymous: {
    id: "studio.access-mode-option.anonymous-label",
    defaultMessage: "Authentication Optional",
  },
  authenticated: {
    id: "studio.access-mode-option.authenticated-label",
    defaultMessage: "Authentication Required",
  },
});

const ACCESS_MODE_DESCRIPTION_MESSAGES = defineMessages({
  defaultAnonymousAllowed: {
    id: "studio.access-mode-option.default-description.anonymous-access-allowed",
    defaultMessage: "The access mode is determined by the security settings in the external config files. The current security configuration seems to allow anonymous access to the service {serviceName}.",
  },
  defaultAnonymousDisallowed: {
    id: "studio.access-mode-option.default-description.anonymous-access-disallowed",
    defaultMessage: "The access mode is determined by the security settings in the external config files. The current security configuration seems to disallow anonymous access to the service {serviceName}.",
  },
  defaultUnknown: {
    id: "studio.access-mode-option.default-description.unknown",
    defaultMessage: "The access mode is determined by the security settings in the external config files. Could not determine whether the current security configuration allows anonymous access to the service {serviceName}.",
  },
  anonymous: {
    id: "studio.access-mode-option.anonymous-description",
    defaultMessage: "Authentication is not required for users accessing this service."
  },
  authenticated: {
    id: "studio.access-mode-option.authenticated-description",
    defaultMessage: "Authentication is required for users accessing this service.",
  },
});

export const labelForAccessMode = (accessMode: AccessMode, intl: InjectedIntl) => {
  switch (accessMode) {
  case AccessMode.DEFAULT:
    return intl.formatMessage(ACCESS_MODE_MESSAGES.default);
  case AccessMode.ANONYMOUS:
    return intl.formatMessage(ACCESS_MODE_MESSAGES.anonymous);
  case AccessMode.AUTHENTICATED:
    return intl.formatMessage(ACCESS_MODE_MESSAGES.authenticated);
  }
}

export const descriptionForAccessMode = (accessMode: AccessMode, defaultAllowsAnonymousAccess: boolean, serviceName: string,
                                         intl: InjectedIntl) => {
  switch (accessMode) {
  case AccessMode.ANONYMOUS:
    return intl.formatMessage(ACCESS_MODE_DESCRIPTION_MESSAGES.anonymous);
  case AccessMode.AUTHENTICATED:
    return intl.formatMessage(ACCESS_MODE_DESCRIPTION_MESSAGES.authenticated);
  case AccessMode.DEFAULT:
  default:
    if (defaultAllowsAnonymousAccess == null) {
      return intl.formatMessage(ACCESS_MODE_DESCRIPTION_MESSAGES.defaultUnknown, {serviceName: serviceName});
    } else if (defaultAllowsAnonymousAccess) {
      return intl.formatMessage(ACCESS_MODE_DESCRIPTION_MESSAGES.defaultAnonymousAllowed, {serviceName: serviceName});
    } else {
      return intl.formatMessage(ACCESS_MODE_DESCRIPTION_MESSAGES.defaultAnonymousDisallowed, {serviceName: serviceName});
    }
  }
}
